/**
 * useDecideAdditionalFee — owner-approver mutation that calls
 * decide-additional-fee. Surfaces structured error codes from the
 * RPC as AppError.code for inline blockers (CLAUDE.md ERROR 4).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export type FeeDecision = 'approve' | 'deny' | 'paid_at_normal_clean_rate'

export interface DecideAdditionalFeeVars {
  requestId:      string
  decision:       FeeDecision
  denialReason?:  string
}

export interface DecideAdditionalFeeResult {
  request_id: string
  status:     'approved' | 'denied' | 'paid_at_normal_clean_rate'
}

export function useDecideAdditionalFee(): UseMutationResult<DecideAdditionalFeeResult, AppError, DecideAdditionalFeeVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<DecideAdditionalFeeResult, AppError, DecideAdditionalFeeVars>({
    mutationFn: async (vars): Promise<DecideAdditionalFeeResult> => {
      // Empty / whitespace-only reason on deny + paid_at_normal_clean_rate
      // is blocked client-side too — the Edge Function and RPC both
      // re-validate, but failing here saves a round-trip and keeps the
      // inline form from showing a successful submit followed by a
      // server-side blocker.
      if ((vars.decision === 'deny' || vars.decision === 'paid_at_normal_clean_rate')
          && (typeof vars.denialReason !== 'string' || vars.denialReason.trim().length === 0)) {
        throw new AppError({
          code:        'MISSING_REASON',
          message:     'denial reason required',
          userMessage: 'A reason is required when denying or marking paid at normal rate.',
          status:      400,
        })
      }

      const { data, error } = await supabase.functions.invoke<DecideAdditionalFeeResult>('decide-additional-fee', {
        body: {
          request_id:     vars.requestId,
          decision:       vars.decision,
          denial_reason:  vars.denialReason?.trim(),
        },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'DECIDE_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Decision returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['additional-fee-requests'] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'MISSING_REASON':
    case 'MISSING_DENIAL_REASON':   return 'A reason is required when denying or marking paid at normal rate.'
    case 'INVALID_DECISION':        return 'That decision is not allowed.'
    case 'INVALID_STATE':           return 'This request is not in a state where that decision can be applied.'
    case 'FORBIDDEN_ROLE':          return 'Your role is not allowed to decide fee requests.'
    case 'FORBIDDEN_ORG':           return 'This request does not belong to your organization.'
    case 'REQUEST_NOT_FOUND':       return 'This fee request could not be found.'
    case 'RATE_LIMITED':            return 'Too many decisions in a row. Try again in a moment.'
    default:                        return detail || 'Could not record the decision.'
  }
}
